import React from 'react';
import { motion } from 'framer-motion';
import promo from '../assets/promo.mp4';

/**
 * SpotlightText Component
 *
 * Wraps its children with an animated gradient overlay to simulate a subtle moving spotlight.
 */
const SpotlightText = ({ children }) => {
  return (
    <motion.div
      className="relative inline-block"
      initial={{ backgroundPosition: '0% 50%' }}
      animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
      transition={{ duration: 4, ease: 'linear', repeat: Infinity }}
      style={{
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
        backgroundSize: '200% 100%',
      }}
    >
      <span className="relative">{children}</span>
    </motion.div>
  );
};

const AboutSection = () => {
  return (
    <section className="py-24 bg-black text-white" aria-labelledby="about-heading">
      <div className="max-w-7xl mx-auto px-4">
        {/* Animated Section Title */}
        <motion.h2
          id="about-heading"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-5xl font-bold text-center mb-8 text-red-500"
        >
          About TheGrillShack
        </motion.h2>
        
        {/* Animated Divider */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.5 }}
          className="w-24 h-1 bg-red-500 mx-auto mb-12 origin-center"
        />

        <div className="flex flex-col md:flex-row items-center gap-12">
          {/* Animated Video Section */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5, duration: 1 }}
            className="w-full md:w-1/2"
          >
            <video
              className="w-full rounded-xl shadow-2xl"
              src={promo}
              autoPlay
              loop
              muted
              playsInline
              title="Promo Video - TheGrillShack"
              aria-label="A promotional video showcasing the Grill Shack restaurant"
            />
          </motion.div>
          
          {/* Animated Text Section with Spotlight Effect on Tagline */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.7, duration: 1 }}
            className="w-full md:w-1/2 text-lg leading-relaxed space-y-6"
          >
            <SpotlightText>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.8 }}
                className="italic text-red-400 text-xl"
              >
                Fresh. Fast. Flavorful.
              </motion.p>
            </SpotlightText>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1, duration: 0.8 }}
            >
              Welcome to TheGrillShack – where every meal is made fresh on-demand. Our expert chefs start crafting your dish the moment you order, ensuring it’s ready in just <strong>10 to 45 minutes</strong>. No pre-cooked meals—only fresh, quality food made just for you.
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.8 }}
            >
              Located in the bustling heart of Victoria Island, Lagos at <strong>1088 Adeola Odeku</strong>, we operate 24 hours a day to satisfy your cravings at any time—day or night.
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4, duration: 0.8 }}
            >
              At TheGrillShack, our passion is to deliver an unforgettable dining experience. Every meal is a celebration of taste, quality, and speedy service. Experience the difference with us—where freshness meets flavor around the clock.
            </motion.p>
          </motion.div>
        </div>
      </div>
      
      {/* SEO Meta Tags */}
      <meta name="description" content="TheGrillShack - Fresh, fast, and flavorful meals made to order. Located in the heart of Victoria Island, Lagos, and open 24/7." />
      <meta name="keywords" content="TheGrillShack, fresh food, fast food, Lagos, 24/7 restaurant, quality food, fast meals" />
      <meta name="robots" content="index, follow" />
    </section>
  );
};

export default AboutSection;
